'use strict'

/**
 * Contract-switch diff for the master-lot change-contract dialog.
 *
 * Answers: "what changes if I switch a master lot's contract?"
 *
 * Pure-fn diff helper for the master lot contract-switch dialog. Compares
 * the lot's current `lot.contract` snapshot to a proposed `newContract`.
 * The dialog uses this for manifest-impact classification, row-level
 * resolution, and the supporting contract-term diff. Same-id
 * ("re-snapshot") cases still return a populated diff before re-applying.
 */

import {ALLOWED_PAID_METALS, safeArray} from './_shared'
import {resolveAssayFeeFromContract, resolveOtherFeesWithLegacy} from './fees'
import {catalogEntriesFromManifest} from '../upstreamMasterLot/manifestCatalog'
import {manifestUnitGrossToz} from '../upstreamMasterLot/manifestWeights'

/** Upstream metal vocabulary: same XRF-all allowlist as METAL_KEYS. */
const METALS_TRACKED_IN_MANIFEST = ALLOWED_PAID_METALS

export const MANIFEST_ROW_SOURCES = Object.freeze({
  SOURCE: 'source',
  CATALOG: 'catalog',
})

export const manifestRowKey = (source, id) => `${source}:${id || 'missing'}`

export const manifestFingerprint = (manifest) => JSON.stringify({
  sourceUnits: safeArray(manifest?.sourceUnits),
  catalogEntries: catalogEntriesFromManifest(manifest),
})

export const removeManifestRowsByKey = (manifest, rowKeys) => {
  const removed = new Set(safeArray(rowKeys).map(String))
  return {
    ...(manifest || {}),
    sourceUnits: safeArray(manifest?.sourceUnits)
      .filter((unit) => !removed.has(manifestRowKey(MANIFEST_ROW_SOURCES.SOURCE, unit?.id))),
    catalogEntries: catalogEntriesFromManifest(manifest)
      .filter((entry) => !removed.has(manifestRowKey(MANIFEST_ROW_SOURCES.CATALOG, entry?.id))),
  }
}

export const computeContractSwitchPreview = (lot, newContract) => {
  if (!newContract) return emptyContractSwitchPreview()

  const currentContract = (lot && lot.contract) || null
  const currentPaidMetals = safeArray(currentContract && currentContract.paidMetals)
  const newPaidMetals = safeArray(newContract.paidMetals)
  const currentPrimaryMetals = safeArray(currentContract && currentContract.primaryMetals)
  const newPrimaryMetals = safeArray(newContract.primaryMetals)

  const currentSet = new Set(currentPaidMetals)
  const newSet = new Set(newPaidMetals)

  const paidMetalsAdded = newPaidMetals.filter((metal) => !currentSet.has(metal))
  const paidMetalsRemoved = currentPaidMetals.filter((metal) => !newSet.has(metal))

  const currentPrimarySet = new Set(currentPrimaryMetals)
  const newPrimarySet = new Set(newPrimaryMetals)
  const primaryMetalsAdded = newPrimaryMetals.filter((metal) => !currentPrimarySet.has(metal))
  const primaryMetalsRemoved = currentPrimaryMetals.filter((metal) => !newPrimarySet.has(metal))

  const metalRatesTierCountDelta = tierCount(newContract) - tierCount(currentContract)

  const existingMilestoneKinds = new Set(safeArray(lot && lot.milestones).map((milestone) => milestone && milestone.kind))
  const milestoneKindsAdded = safeArray(newContract.expectedMilestones)
    .map((milestone) => milestone && milestone.kind)
    .filter((kind) => kind && !existingMilestoneKinds.has(kind))

  const manifestRows = classifyManifestRows(lot, newContract)

  return {
    isSamePartner: Boolean(currentContract && currentContract.refiningPartnerId === newContract.refiningPartnerId),
    isSameContract: Boolean(currentContract && currentContract.id === newContract.id),
    paidMetalsAdded,
    paidMetalsRemoved,
    primaryMetalsAdded,
    primaryMetalsRemoved,
    lotWeightRequirementsChanged: stableSerializeSchedule(currentContract?.lotWeightRequirements)
      !== stableSerializeSchedule(newContract.lotWeightRequirements),
    metalRatesTierCountDelta,
    milestoneKindsAdded,
    manifestRows,
    compatibleManifestRows: manifestRows.filter((row) => row.compatible),
    incompatibleManifestRows: manifestRows.filter((row) => !row.compatible),
    feeWaivedChangedByMetal: feeWaivedChangedByMetal(currentContract, newContract),
    minimumReturnChangedByMetal: minimumReturnChangedByMetal(currentContract, newContract),
    assayFeeChangedByMetal: assayFeeChangedByMetal(currentContract, newContract),
    feeShapeChanges: feeShapeChanges(currentContract, newContract),
    validityWindowChanged: (
      (currentContract && currentContract.effectiveFrom) !== (newContract && newContract.effectiveFrom)
      || (currentContract && currentContract.effectiveTo) !== (newContract && newContract.effectiveTo)
    ),
    settlementSlaChangedByMetal: settlementSlaChangedByMetal(currentContract, newContract),
    currencyChanged: resolvedCurrency(currentContract) !== resolvedCurrency(newContract),
    materialAcceptanceCriteriaChanged: (
      resolvedMaterialAcceptance(currentContract) !== resolvedMaterialAcceptance(newContract)
    ),
    referenceFeeScheduleChanged: (
      stableSerializeSchedule(resolvedReferenceFeeSchedule(currentContract))
      !== stableSerializeSchedule(resolvedReferenceFeeSchedule(newContract))
    ),
    slaModifiersChanged: (
      stableSerializeModifiers(currentContract && currentContract.slaModifiers)
      !== stableSerializeModifiers(newContract && newContract.slaModifiers)
    ),
    // null↔null is baseline; flag any transition into/out of
    // cross-metal qualification AND any change between two populated
    // qualifying metals.
    tierQualifyingMetalChanged: (
      (currentContract && currentContract.tierQualifyingMetal || null)
      !== (newContract && newContract.tierQualifyingMetal || null)
    ),
    rowTierQualifyingMetalChangedByMetal: rowTierQualifyingMetalChangedByMetal(currentContract, newContract),
    // Per-metal refining-fee basis transitions (e.g. credited-fine-toz
    // → net-wt-received). Same fee-shape delta category as
    // feeWaivedChangedByMetal in the diff card UI.
    feeBasisChangedByMetal: feeBasisChangedByMetal(currentContract, newContract),
  }
}

const tierCount = (contract) => safeArray(contract && contract.metal_rates).length

const metalHasFeeWaived = (contract, metal) => {
  const metalKey = String(metal).toLowerCase()
  return safeArray(contract && contract.metal_rates).some((row) => (
    row && String(row.metalType).toLowerCase() === metalKey && row.feeWaived === true
  ))
}

const minimumReturnByMetal = (contract) => {
  const byMetal = {}
  for (const row of safeArray(contract && contract.metal_rates)) {
    if (!row || !row.metalType) continue
    const metal = String(row.metalType).toLowerCase()
    if (byMetal[metal] === undefined && Number.isFinite(row.minimumReturnToz)) {
      byMetal[metal] = row.minimumReturnToz
    }
  }
  return byMetal
}

const settlementSlaByMetal = (contract) => {
  const byMetal = {}
  for (const row of safeArray(contract && contract.metal_rates)) {
    if (!row || !row.metalType) continue
    const metal = String(row.metalType).toLowerCase()
    if (Number.isFinite(row.settlementBusinessDays)) byMetal[metal] = row.settlementBusinessDays
  }
  return byMetal
}

const settlementSlaChangedByMetal = (currentContract, newContract) => {
  const current = settlementSlaByMetal(currentContract)
  const next = settlementSlaByMetal(newContract)
  const metals = new Set([...Object.keys(current), ...Object.keys(next)])
  return [...metals].filter((metal) => current[metal] !== next[metal])
}

const minimumReturnChangedByMetal = (currentContract, newContract) => {
  const current = minimumReturnByMetal(currentContract)
  const next = minimumReturnByMetal(newContract)
  const metals = new Set([...Object.keys(current), ...Object.keys(next)])
  return [...metals].filter((metal) => current[metal] !== next[metal])
}

const assayFeeSnapshot = (contract) => {
  if (!contract) return {}
  const map = resolveAssayFeeFromContract(contract)
  const snapshot = {}
  for (const [metal, row] of Object.entries(map)) {
    snapshot[metal] = {
      feeUsd: row.feeUsd,
      optInAtSalesOrder: Boolean(row.optInAtSalesOrder),
      tierQualifyingMetal: row.tierQualifyingMetal || null,
      purityTiers: Array.isArray(row.purityTiers)
        ? row.purityTiers.map((tier) => ({
            minPct: tier.minPct,
            maxPct: tier.maxPct,
            feeUsd: tier.feeUsd,
          }))
        : null,
    }
  }
  return snapshot
}

// Snapshot notes + purityTiers alongside flat fee fields so a switch
// that only changes notes or band shape still flags. Band ordering is
// significant (declared in ascending purity); preserve order so
// stable-stringify catches re-orderings.
const normalizePurityTiers = (rawTiers) => {
  if (!Array.isArray(rawTiers) || rawTiers.length === 0) return null
  return rawTiers.map((band) => (band && typeof band === 'object'
    ? {minPct: band.minPct, maxPct: band.maxPct, perTozUsd: band.perTozUsd, minimumUsd: band.minimumUsd}
    : band
  ))
}

const normalizeOtherFeeRow = (row) => ({
  code: row && row.code,
  kind: row && row.kind,
  flatUsd: row && row.flatUsd,
  perTozUsd: row && row.perTozUsd,
  minimumUsd: row && row.minimumUsd,
  conditional: Boolean(row && row.conditional),
  notes: row && typeof row.notes === 'string' ? row.notes : null,
  purityTiers: normalizePurityTiers(row && row.purityTiers),
})

const otherFeesSnapshot = (contract) => {
  const snapshot = {}
  for (const row of resolveOtherFeesWithLegacy(contract)) {
    if (row && row.code) snapshot[row.code] = normalizeOtherFeeRow(row)
  }
  return snapshot
}

const feeShapeChanges = (currentContract, newContract) => {
  const current = otherFeesSnapshot(currentContract)
  const next = otherFeesSnapshot(newContract)
  const codes = new Set([...Object.keys(current), ...Object.keys(next)])
  return [...codes].filter((code) => (
    JSON.stringify(current[code]) !== JSON.stringify(next[code])
  ))
}

const assayFeeChangedByMetal = (currentContract, newContract) => {
  const current = assayFeeSnapshot(currentContract)
  const next = assayFeeSnapshot(newContract)
  const metals = new Set([...Object.keys(current), ...Object.keys(next)])
  return [...metals].filter((metal) => {
    const a = current[metal]
    const b = next[metal]
    return JSON.stringify(a) !== JSON.stringify(b)
  })
}

const feeWaivedChangedByMetal = (currentContract, newContract) => {
  const metals = new Set([
    ...safeArray(currentContract && currentContract.paidMetals),
    ...safeArray(newContract && newContract.paidMetals),
  ])
  return [...metals].filter((metal) => (
    metalHasFeeWaived(currentContract, metal) !== metalHasFeeWaived(newContract, metal)
  ))
}

// Snapshot is the set of distinct feeBasis values across the metal's
// tier rows. Missing/absent normalizes to 'creditedFineToz' so
// pre-extension snapshots compare clean against extension-stamped
// fixtures.
const feeBasisSetForMetal = (contract, metal) => {
  const target = String(metal).toLowerCase()
  const bases = new Set()
  for (const row of safeArray(contract && contract.metal_rates)) {
    if (!row || String(row.metalType).toLowerCase() !== target) continue
    bases.add((row.feeBasis && typeof row.feeBasis === 'string') ? row.feeBasis : 'creditedFineToz')
  }
  return [...bases].sort().join('|')
}

const feeBasisChangedByMetal = (currentContract, newContract) => {
  const metals = new Set([
    ...safeArray(currentContract && currentContract.paidMetals),
    ...safeArray(newContract && newContract.paidMetals),
  ])
  return [...metals].filter((metal) => (
    feeBasisSetForMetal(currentContract, metal) !== feeBasisSetForMetal(newContract, metal)
  ))
}

const rowTierQualifierSnapshot = (contract, metal) => safeArray(contract?.metal_rates)
  .filter((row) => row && String(row.metalType).toLowerCase() === metal)
  .map((row) => ({
    tierMin: row.tierMin,
    tierMax: row.tierMax,
    tierQualifyingMetal: row.tierQualifyingMetal || null,
  }))

const rowTierQualifyingMetalChangedByMetal = (currentContract, newContract) => {
  const metals = new Set([
    ...safeArray(currentContract?.paidMetals),
    ...safeArray(newContract?.paidMetals),
  ])
  return [...metals].filter((metal) => (
    JSON.stringify(rowTierQualifierSnapshot(currentContract, metal))
    !== JSON.stringify(rowTierQualifierSnapshot(newContract, metal))
  ))
}

// Canonical "no diff" shape. Single source for every field the preview
// carries — the change-contract dialog reuses it for its no-selection state
// so the two never drift.
export const emptyContractSwitchPreview = () => ({
  isSamePartner: false,
  isSameContract: false,
  paidMetalsAdded: [],
  paidMetalsRemoved: [],
  primaryMetalsAdded: [],
  primaryMetalsRemoved: [],
  lotWeightRequirementsChanged: false,
  metalRatesTierCountDelta: 0,
  milestoneKindsAdded: [],
  manifestRows: [],
  compatibleManifestRows: [],
  incompatibleManifestRows: [],
  feeWaivedChangedByMetal: [],
  minimumReturnChangedByMetal: [],
  assayFeeChangedByMetal: [],
  feeShapeChanges: [],
  validityWindowChanged: false,
  settlementSlaChangedByMetal: [],
  currencyChanged: false,
  materialAcceptanceCriteriaChanged: false,
  referenceFeeScheduleChanged: false,
  slaModifiersChanged: false,
  tierQualifyingMetalChanged: false,
  rowTierQualifyingMetalChangedByMetal: [],
  feeBasisChangedByMetal: [],
})

// Whether a computed preview represents zero effective change (same contract,
// every diff dimension empty). Co-located with emptyContractSwitchPreview so
// the participating-field roster lives in exactly one place.
export const isNoOpContractSwitch = (preview) => {
  // Normalize against the canonical empty shape so partial/stale previews can't
  // throw on a missing array field once isSameContract is truthy.
  const p = {...emptyContractSwitchPreview(), ...(preview || {})}
  return (
    p.isSameContract &&
    !p.paidMetalsAdded.length &&
    !p.paidMetalsRemoved.length &&
    p.primaryMetalsAdded.length === 0 &&
    p.primaryMetalsRemoved.length === 0 &&
    !p.lotWeightRequirementsChanged &&
    p.metalRatesTierCountDelta === 0 &&
    !p.milestoneKindsAdded.length &&
    !p.feeWaivedChangedByMetal.length &&
    !p.minimumReturnChangedByMetal.length &&
    !p.assayFeeChangedByMetal.length &&
    !p.feeShapeChanges.length &&
    !p.feeBasisChangedByMetal.length &&
    !p.validityWindowChanged &&
    !p.settlementSlaChangedByMetal.length &&
    !p.currencyChanged &&
    !p.materialAcceptanceCriteriaChanged &&
    !p.referenceFeeScheduleChanged &&
    !p.slaModifiersChanged &&
    !p.tierQualifyingMetalChanged &&
    !p.rowTierQualifyingMetalChangedByMetal.length
  )
}

// Legacy contracts may not carry currency; treat absence as USD so
// USD→USD switches don't false-positive.
const resolvedCurrency = (contract) => (contract && contract.currency) || 'USD'

// Legacy contracts may not carry the field; normalize to null
// (matching editor default) so null↔null switches don't flag.
const resolvedMaterialAcceptance = (contract) => {
  if (!contract) return null
  const value = contract.materialAcceptanceCriteria
  return typeof value === 'string' ? value : null
}

// Coarse top-level "did the schedule change at all" signal.
// Stable-stringify by alphabetical keys so re-ordered sub-fields
// don't read as a change.
const stableSerializeSchedule = (schedule) => {
  if (schedule === null || schedule === undefined) return 'null'
  if (typeof schedule !== 'object' || Array.isArray(schedule)) return JSON.stringify(schedule)
  const keys = Object.keys(schedule).sort()
  if (keys.length === 0) return 'null'
  return JSON.stringify(keys.map((key) => [key, schedule[key]]))
}

const resolvedReferenceFeeSchedule = (contract) => (
  contract && contract.referenceFeeSchedule ? contract.referenceFeeSchedule : null
)

// Treat absence and [] as the canonical "no modifiers" so null↔[]
// doesn't false-positive. Modifier ordering is significant — no
// array sort before serialization.
const stableSerializeModifiers = (modifiers) => {
  if (!Array.isArray(modifiers) || modifiers.length === 0) return 'null'
  return JSON.stringify(modifiers.map((mod) => {
    if (!mod || typeof mod !== 'object') return mod
    return {
      addBusinessDays: mod.addBusinessDays,
      notes: mod.notes || null,
      when: mod.when || null,
    }
  }))
}

const classifyManifestRows = (lot, contract) => {
  const paidMetals = new Set(safeArray(contract?.paidMetals).map((metal) => String(metal).toLowerCase()))
  const manifestRows = manifestRowsFrom(lot)
  const qualifiers = manifestQualifiers(manifestRows.map(({unit}) => unit))
  return manifestRows.map(({source, unit}) => (
    classifyManifestRow(source, unit, paidMetals, contract, qualifiers)
  ))
}

const manifestRowsFrom = (lot) => [
  ...safeArray(lot?.manifest?.sourceUnits)
    .map((unit) => ({source: MANIFEST_ROW_SOURCES.SOURCE, unit})),
  ...catalogEntriesFromManifest(lot?.manifest)
    .map((unit) => ({source: MANIFEST_ROW_SOURCES.CATALOG, unit})),
]

const classifyManifestRow = (source, unit, paidMetals, contract, qualifiers) => {
  const metals = contributingMetals(unit)
  const issues = []
  metals.forEach((metal) => {
    if (!paidMetals.has(metal)) {
      issues.push({
        code: 'metal_not_paid',
        metal,
        message: `${metal.toUpperCase()} is present but not paid by the proposed contract.`,
      })
      return
    }
    if (!hasMatchingRateTier(contract, metal, qualifiers)) {
      issues.push({
        code: 'missing_rate_tier',
        metal,
        message: `${metal.toUpperCase()} is paid but has no proposed contract rate tier.`,
      })
    }
  })
  const id = unit?.id || null
  return {
    key: manifestRowKey(source, id),
    id,
    source,
    kind: unit?.kind || null,
    label: manifestRowLabel(source, unit),
    compatible: issues.length === 0,
    issues,
  }
}

const contributingMetals = (unit) => {
  const purity = unit?.purity || {}
  return METALS_TRACKED_IN_MANIFEST.filter((metal) => (
    Number.isFinite(purity[metal]) && purity[metal] > 0
  ))
}

const hasMatchingRateTier = (contract, metal, qualifiers) => safeArray(contract?.metal_rates)
  .filter((row) => row && String(row.metalType).toLowerCase() === metal)
  .some((row) => {
    const qualifyValue = tierQualifyValue(contract, row, metal, qualifiers)
    if (!Number.isFinite(qualifyValue)) return false
    const min = Number.isFinite(row.tierMin) ? row.tierMin : 0
    const max = Number.isFinite(row.tierMax) ? row.tierMax : Infinity
    return qualifyValue >= min && qualifyValue < max
  })

const tierQualifyValue = (contract, row, metal, qualifiers) => {
  if (contract?.tierDimension !== 'purityPct') return qualifiers.grossToz
  const qualifyingMetal = String(row?.tierQualifyingMetal || contract?.tierQualifyingMetal || metal)
    .toLowerCase()
  return qualifiers.purityPctByMetal[qualifyingMetal]
}

const manifestQualifiers = (units) => {
  const grossToz = units.reduce((sum, unit) => {
    const weight = manifestUnitGrossToz(unit)
    return sum + (weight > 0 ? weight : 0)
  }, 0)
  const purityPctByMetal = {}
  METALS_TRACKED_IN_MANIFEST.forEach((metal) => {
    if (grossToz <= 0) return
    const weightedPurity = units.reduce((sum, unit) => {
      const weight = manifestUnitGrossToz(unit)
      const positiveWeight = weight > 0 ? weight : 0
      const purity = Number.isFinite(unit?.purity?.[metal]) ? unit.purity[metal] : 0
      return sum + positiveWeight * purity
    }, 0)
    purityPctByMetal[metal] = weightedPurity / grossToz
  })
  return {grossToz, purityPctByMetal}
}

const manifestRowLabel = (source, unit) => {
  if (source === MANIFEST_ROW_SOURCES.CATALOG) {
    return unit?.name || unit?.description || unit?.sku || unit?.id || 'Catalog row'
  }
  if (unit?.sourceLotCode && unit?.barNumber) return `${unit.sourceLotCode} · bar ${unit.barNumber}`
  return unit?.sourceLotCode || unit?.id || 'Source unit'
}
